/**
 * LeaveConfirmDialog.tsx — the shared "leave this step?" confirmation.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient-journey
 * navigation resilience (feat/patient-journey-navigation-resilience).
 *
 * WHY its own component (not `ConfirmDialog` directly): the copy is FIXED
 * (title, body, "Stay here" / "Leave this step") — every dirty surface in the
 * journey/checkout/profile shares the exact same wording, so there is nothing
 * per-caller to configure beyond `open`/`onStay`/`onLeave`. Built on the shared
 * `DialogShell` frame (overlay + Escape + focus handling — see
 * `ui/ConfirmDialog.tsx`) so it matches the rest of the portal's modal grammar.
 * "Stay here" is the safe default: it is the primary CTA and Escape/overlay
 * click both resolve to it, never to the destructive "Leave this step".
 *
 * THE JOURNEY VARIANT (CTO, 2026-09-25 — a RETENTION moment, not a warning).
 * *"Instead of this, 'you're a couple of steps away' (maybe it only takes around
 * 2 minutes to submit), give them the option to stay here or continue. As a
 * hyperlink, give them the option to leave the step if needed … a bit of a
 * bigger modal and some sort of animation … even offer the request callback
 * option here."* Opt-in through `journey`: the patient questionnaire journey
 * (Step 0's CaptureStep and QuestionFlow's question screens) passes it; the
 * profile and checkout's address form do not, and render EXACTLY the dialog
 * above, byte for byte (pinned in `leaveStepRetention.spec.tsx`). One dialog
 * with a variant, never a second component to keep in step — the guard, the
 * `onStay`/`onLeave` contract and the Escape-means-stay rule are shared.
 *
 *   - THE WORDS ARE TRUE TO WHERE THEY ARE. Heading and line come from
 *     `journey/progressCopy.ts` (`leaveStepCopy`), off the same measure as the
 *     progress line on the page behind: a real count of the questions left
 *     only where the line would print a number, "nearly there" only where the
 *     line has said so, and otherwise the length of the whole check
 *     (`CHECK_MINUTES`, the "2-minute check" Step 0 already promises). The
 *     honest note stays, short and secondary: leaving loses what is on this
 *     page. It describes the "Leave this step" link (`aria-describedby`).
 *   - "STAY AND FINISH" IS THE BUTTON; "LEAVE THIS STEP" IS A QUIET LINK — but a
 *     real `<button>` with a 44px target, and it calls `onLeave` exactly as the
 *     old button did. Escape and an overlay click still STAY; nothing here can
 *     leave on a key the patient did not mean.
 *   - THE CALL-BACK IS THE PAGE'S OWN (journey/JourneyCallbackContext.tsx), the
 *     same hand-off "Why we ask" makes: in ONE click this dialog stays (its
 *     `onStay` — the blocked navigation is cancelled, the answers are kept) and
 *     the offer's own call-back dialog opens, in the same commit — one dialog at
 *     a time, the same `CallbackForm`, the same POST and lead, and never a
 *     navigation. Once a call-back has been asked for, the row thanks instead
 *     (a second request would mint a second ticket). No provider, or no offer
 *     published under it (the capture loader) — no row and no hairline. And
 *     one dialog at a time the OTHER way too: Back pressed while that call-back
 *     dialog is up closes it and stays, rather than opening this dialog
 *     underneath it (review, 2026-09-25; `callback.isOpen`).
 *   - LOOK AND MOTION. A wider panel (`min(36rem, 100%)`), centred, a warm
 *     ivory wash into the white card, a light Fraunces heading and a small
 *     finish-flag badge: `gps-jux-leave-*` in tokens.css, reached through
 *     `DialogShell`'s additive `className`. The panel eases in with the "Why we
 *     ask" entrance (`gps-jux-why-enter`) and the badge pops in with the
 *     celebration's tick (`gps-check-in`) — both only when
 *     `usePrefersReducedMotion` allows, with the global kill as the second lock.
 *   - FOCUS. `trapFocus`: this dialog opens over a question whose answers submit
 *     on one key. Stay returns focus to what had it (DialogShell). After the
 *     call-back hand-off, closing that dialog without a send returns focus to
 *     the same place (`open(returnFocusTo)`), because the control that opened
 *     it lived in this dialog, which has gone.
 *   - NOTHING NEW REACHES THE DATALAYER. No tracking here: a new event is a
 *     tracking change, and those go through Salman. The call-back is the
 *     existing lane with whatever it already records.
 */
import { useEffect, useId, useRef } from 'react';
import { Button } from '../ui/kit';
import { DialogShell } from '../ui/ConfirmDialog';
import { usePrefersReducedMotion } from '../ui/motion';
import { useJourneyCallback } from '../journey/JourneyCallbackContext';
import { CALLBACK_OFFER_ACTION, CALLBACK_OFFER_LEAD, CALLBACK_OFFER_THANKS } from '../journey/JourneyCallbackOffer';
import { JourneyButton } from '../journey/JourneyUi';
import { leaveStepCopy, type RemainingQuestions } from '../journey/progressCopy';

/** The journey variant's two actions and its note — exported for the specs. */
export const LEAVE_STEP_STAY = 'Stay and finish';
export const LEAVE_STEP_LEAVE = 'Leave this step';
export const LEAVE_STEP_NOTE = 'Leaving now loses what you’ve entered on this page.';

/** Opts a caller into the journey variant. */
export interface LeaveStepJourney {
  /**
   * How much of the questionnaire is left (QuestionFlow, off the same measure
   * as its progress line), or `null` where that is not known — Step 0, and the
   * leading height/weight re-ask.
   */
  remaining: RemainingQuestions | null;
  /**
   * `false` when nothing is being left: the dialog opened on EXIT INTENT (the
   * pointer heading for the tab strip, `useExitIntent`), not on a navigation, so
   * "Leave this step" and its "loses what you've entered" note would be a
   * control with nothing to do and a warning about nothing (2026-09-26). The
   * patient closes the tab themselves if they mean to. Absent → shown.
   */
  leaveLink?: boolean;
}

export function LeaveConfirmDialog({
  open,
  onStay,
  onLeave,
  journey,
}: {
  open: boolean;
  /** "Stay here" — the safe default (Escape/overlay click also resolve here). */
  onStay: () => void;
  /** "Leave this step" — proceeds, discarding the unsaved changes. */
  onLeave: () => void;
  /** The patient journey's retention variant (see the header). Absent → the classic dialog, unchanged. */
  journey?: LeaveStepJourney;
}) {
  if (!open) return null;
  if (journey)
    return (
      <JourneyLeaveDialog remaining={journey.remaining} leaveLink={journey.leaveLink !== false} onStay={onStay} onLeave={onLeave} />
    );
  return (
    <DialogShell title="Leave this step?" onCancel={onStay}>
      <p style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: '0 0 var(--gps-s-3)' }}>
        You have changes on this page that haven&rsquo;t been saved. If you leave now, those changes
        will be lost.
      </p>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="button"
          variant="cta"
          onClick={onStay}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
        >
          Stay here
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onLeave}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}
        >
          Leave this step
        </Button>
      </div>
    </DialogShell>
  );
}

/**
 * The journey variant. Its own component so its hooks (the call-back context,
 * reduced motion) run only for a caller that asked for it: the classic dialog
 * above calls none, which is what keeps it exactly as it was.
 */
function JourneyLeaveDialog({
  remaining,
  leaveLink,
  onStay,
  onLeave,
}: {
  remaining: RemainingQuestions | null;
  leaveLink: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const callback = useJourneyCallback();
  const reducedMotion = usePrefersReducedMotion();
  const noteId = useId();
  // What had focus when the dialog opened (the in-app Back, or the tick the
  // patient had just made) — captured on the FIRST render, as DialogShell does,
  // before anything inside the dialog can take it. It is where a call-back
  // dialog closed without a send hands focus back to.
  const openerRef = useRef<Element | null | undefined>(undefined);
  if (openerRef.current === undefined) openerRef.current = document.activeElement;
  // BACK WHILE THE CALL-BACK DIALOG IS UP CLOSES IT (review, 2026-09-25). The
  // patient handed off from here, then pressed Back again — on Android the
  // usual way to dismiss a dialog. Rendering would stack this dialog UNDER the
  // call-back one (same z-index layer, earlier in the DOM) and DialogShell would
  // pull focus behind the scrim. So this mount draws nothing and does what Back
  // means on a dialog: the navigation is cancelled (`onStay`, answers kept) and
  // the call-back dialog closes as its "Not now" would, focus and all. Decided
  // on the FIRST render, so the dialog can never flash in once that one closes;
  // `yielded` makes it exactly once (StrictMode runs a mount effect twice).
  const yieldToCallback = useRef(callback?.isOpen === true).current;
  const yielded = useRef(false);
  useEffect(() => {
    if (!yieldToCallback || yielded.current) return;
    yielded.current = true;
    onStay();
    callback?.close();
  }, [yieldToCallback, onStay, callback]);
  if (yieldToCallback) return null;
  const copy = leaveStepCopy(remaining);

  // ONE DIALOG AT A TIME, AND NOTHING LOST. `onStay` cancels the blocked
  // navigation (or drops the deferred in-app Back) exactly as "Stay and
  // finish" does, and `open()` shows the page's call-back dialog. Both land in
  // this click's single React commit, so this dialog is gone in the same paint
  // the call-back dialog appears in.
  function requestCallback() {
    if (!callback) return;
    const opener = openerRef.current;
    onStay();
    callback.open(opener instanceof HTMLElement && opener !== document.body ? opener : null);
  }

  return (
    <DialogShell
      title={copy.heading}
      onCancel={onStay}
      width="min(36rem, 100%)"
      className={reducedMotion ? 'gps-jux-leave-dialog' : 'gps-jux-leave-dialog gps-jux-why-enter'}
      // The dialog sits in the question screen, before its answers in the DOM,
      // and a single-select answer submits on one key: Tab must stay inside.
      trapFocus
    >
      {/* Drawn ABOVE the heading by the panel's flex order (tokens.css); after it
          in the DOM, so the heading is still the first thing read. */}
      <span
        className="gps-jux-leave-badge"
        aria-hidden="true"
        style={reducedMotion ? undefined : { animation: 'gps-check-in 480ms var(--gps-ease) 80ms both' }}
      >
        <FinishFlagIcon />
      </span>
      <div className="gps-jux-leave-body">
        <p className="gps-jux-leave-line">{copy.line}</p>
        <div className="gps-jux-leave-actions">
          <JourneyButton type="button" variant="primary" className="gps-jux-leave-stay" onClick={onStay}>
            {LEAVE_STEP_STAY}
          </JourneyButton>
          {leaveLink ? (
            <>
              <button type="button" className="gps-jux-leave-link" aria-describedby={noteId} onClick={onLeave}>
                {LEAVE_STEP_LEAVE}
              </button>
              <p id={noteId} className="gps-jux-leave-note">
                {LEAVE_STEP_NOTE}
              </p>
            </>
          ) : null}
        </div>
        {callback ? (
          <>
            <hr className="gps-jux-why-rule" />
            <div className="gps-jux-leave-callback">
              {callback.requested ? (
                <p className="gps-jux-why-thanks">{CALLBACK_OFFER_THANKS}</p>
              ) : (
                <>
                  <p className="gps-jux-why-callback-lead">{CALLBACK_OFFER_LEAD}</p>
                  <JourneyButton
                    type="button"
                    outline
                    aria-haspopup="dialog"
                    onClick={requestCallback}
                    style={{ width: 'auto' }}
                  >
                    {CALLBACK_OFFER_ACTION}
                  </JourneyButton>
                </>
              )}
            </div>
          </>
        ) : null}
      </div>
    </DialogShell>
  );
}

/** A small finish flag — decoration only (the badge is `aria-hidden`). */
function FinishFlagIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={24} height={24} xmlns="http://www.w3.org/2000/svg">
      <path d="M6 21V4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 4.5h11.5l-2.25 4 2.25 4H6z" fill="currentColor" />
    </svg>
  );
}
